import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { ConfirmModal, MoreMenu, StateView, type MenuAction } from '@/card/record'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  Grip,
  LayerButtons,
  Notice,
  PrimaryButton,
  ReadonlyValue,
  RowMenu,
  SkeletonList,
  SwatchPick,
  colorName,
  colorToken,
  isForbidden,
  moveId,
  useAdminEditing,
} from './AdminShell'
import {
  addFunnelStage,
  createAdminFunnel,
  fetchAdminFunnels,
  removeFunnelStage,
  reorderFunnelStages,
  updateAdminFunnel,
  updateFunnelStage,
  type AdminFunnel,
  type AdminStage,
} from './api'

/**
 * Воронки и этапы (тикет #103, макет AdmPipelines.dc.html).
 *
 * Доска сделок читает состав воронки на каждом запросе, поэтому этап, добавленный здесь,
 * виден менеджерам следующим же открытием доски — без релиза. Открытые этапы
 * переставляются перетаскиванием и с клавиатуры (фокус на ручке, ↑/↓); закрывающие
 * («выиграна», «проиграна») всегда стоят в конце, их не двигают и не удаляют.
 *
 * Этап, на котором есть сделки или через который они проходили, сервер удалить не даст.
 * Открытые сделки экран знает и называет причину заранее — пункт меню виден, но
 * недоступен; про историю переходов он не знает, и тогда отказ приходит словами сервера.
 *
 * Правка вероятности этапа переносится на его открытые сделки (#103): прогноз иначе
 * зависел бы от того, когда сделка попала в колонку. Панель правки говорит об этом до
 * сохранения — числом сделок, которые изменятся.
 */
export function FunnelsPage() {
  const { t } = useApp()
  const editing = useAdminEditing()
  const queryClient = useQueryClient()
  const funnels = useQuery({ queryKey: ['admin-funnels'], queryFn: ({ signal }) => fetchAdminFunnels(signal) })
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [renaming, setRenaming] = useState<AdminFunnel | null>(null)
  const [adding, setAdding] = useState(false)

  const list = funnels.data ?? []
  const selected = list.find((f) => f.id === selectedId) ?? list.find((f) => f.isDefault) ?? list[0] ?? null
  const forbidden = isForbidden(funnels.error)

  const done = (funnel?: AdminFunnel) => {
    editing.clear()
    if (funnel) setSelectedId(funnel.id)
    void queryClient.invalidateQueries({ queryKey: ['admin-funnels'] })
    // The board and its funnel list are cached under their own keys; a stage added here
    // must not wait for their stale time to show up.
    void queryClient.invalidateQueries({ queryKey: ['deal-funnels'] })
    void queryClient.invalidateQueries({ queryKey: ['deal-board'] })
  }
  const props = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { active?: boolean; isDefault?: boolean } }) => updateAdminFunnel(id, body),
    onSuccess: done,
    onError: editing.fail,
  })

  const openDeals = (funnel: AdminFunnel) => funnel.stages.reduce((sum, stage) => sum + stage.openDeals, 0)
  const funnelMenu = (funnel: AdminFunnel): MenuAction[] => [
    { id: 'rename', label: t('adm.funnel.rename'), icon: 'adm-edit', onSelect: () => setRenaming(funnel) },
    ...(funnel.isDefault
      ? []
      : [{ id: 'default', label: t('adm.funnel.makeDefault'), icon: 'check', onSelect: () => props.mutate({ id: funnel.id, body: { isDefault: true } }) } as MenuAction]),
    funnel.active
      ? {
          id: 'archive',
          label: t('adm.funnel.archive'),
          icon: 'adm-archive',
          disabledReason: funnel.isDefault ? t('adm.funnel.archive.default') : undefined,
          onSelect: () => props.mutate({ id: funnel.id, body: { active: false } }),
        }
      : { id: 'restore', label: t('adm.funnel.restore'), icon: 'refresh', onSelect: () => props.mutate({ id: funnel.id, body: { active: true } }) },
  ]

  return (
    <AdminPage
      titleKey="nav.adminFunnels"
      leadKey="adm.funnel.lead"
      readOnly={!editing.mayEdit}
      roKey="adm.funnel.ro"
      forbidden={forbidden}
      primary={editing.mayEdit && selected && <PrimaryButton label={t('admin.stage.add')} onClick={() => setAdding(true)} />}
    >
      <Notice text={editing.notice} />
      <AdminState
        isLoading={funnels.isLoading}
        error={funnels.error}
        onRetry={() => void funnels.refetch()}
        forbiddenTitleKey="adm.funnel.forbidden"
        errorTitleKey="adm.funnel.error"
        skeleton={<div className="adm-split is-wide"><SkeletonList rows={4} /><SkeletonList rows={7} /></div>}
      >
        {list.length === 0 ? (
          <StateView boxed kind="empty" title={t('adm.funnel.empty')} description={t('adm.funnel.empty.text')}
                     actions={editing.mayEdit && (
                       <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCreating(true)}>
                         <Icon name="plus" size="sm" />{t('admin.funnel.add')}
                       </button>
                     )} />
        ) : (
          <div className="adm-split is-wide">
            <section className="panel" aria-labelledby="pl-h" style={{ overflow: 'hidden' }}>
              <div className="panel-h">
                <span className="h2" id="pl-h">{t('adm.funnel.list')}</span>
                <span className="count">{list.length}</span>
                <span className="spacer" />
                {editing.mayEdit && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCreating(true)}>
                    <Icon name="plus" size="sm" />{t('adm.funnel.addShort')}
                  </button>
                )}
              </div>
              <div role="listbox" aria-label={t('adm.funnel.list')}>
                {list.map((funnel) => {
                  const current = funnel.id === selected?.id
                  return (
                    <div key={funnel.id} role="option" aria-selected={current} tabIndex={0}
                         className={current ? 'role-item is-active' : 'role-item'}
                         onClick={() => setSelectedId(funnel.id)}
                         onKeyDown={(event) => {
                           if (event.key === 'Enter' || event.key === ' ') {
                             event.preventDefault()
                             setSelectedId(funnel.id)
                           }
                         }}>
                      <span className="stack" style={{ gap: 0, minWidth: 0 }}>
                        <span className={!funnel.active ? 'muted ellipsis' : current ? 'strong ellipsis' : 'ellipsis'}>{funnel.name}</span>
                        <span className="t-sm muted">{t('adm.funnel.meta', { stages: funnel.stages.length, open: openDeals(funnel) })}</span>
                      </span>
                      <span className="row" style={{ gap: 6, alignSelf: 'center' }}>
                        {funnel.isDefault && <span className="badge b-accent">{t('adm.funnel.default')}</span>}
                        {!funnel.active && <span className="badge"><Icon name="adm-archive" />{t('adm.funnel.archived')}</span>}
                        {funnel.active && <span className="count-pill" title={t('admin.stage.openDeals')}>{openDeals(funnel)}</span>}
                      </span>
                    </div>
                  )
                })}
              </div>
              <div className="panel-f muted">{t('adm.funnel.archiveNote')}</div>
            </section>
            {selected && (
              <FunnelEditor key={selected.id} funnel={selected} mayEdit={editing.mayEdit} menu={funnelMenu(selected)}
                            onDone={done} onFailed={editing.fail} />
            )}
          </div>
        )}
      </AdminState>
      {creating && <NewFunnelModal onClose={() => setCreating(false)} onSaved={(funnel) => {
        setCreating(false)
        done(funnel)
      }} />}
      {renaming && <RenameFunnelModal funnel={renaming} onClose={() => setRenaming(null)} onSaved={(funnel) => {
        setRenaming(null)
        done(funnel)
      }} />}
      {adding && selected && (
        <StageSheet funnel={selected} stage={null} onClose={() => setAdding(false)} onSaved={(funnel) => {
          setAdding(false)
          done(funnel)
        }} />
      )}
    </AdminPage>
  )
}

function FunnelEditor({
  funnel,
  mayEdit,
  menu,
  onDone,
  onFailed,
}: {
  funnel: AdminFunnel
  mayEdit: boolean
  menu: MenuAction[]
  onDone: (funnel?: AdminFunnel) => void
  onFailed: (error: unknown) => void
}) {
  const { t, locale } = useApp()
  const [dragging, setDragging] = useState<string | null>(null)
  const [editingStage, setEditingStage] = useState<AdminStage | null>(null)
  const [removing, setRemoving] = useState<AdminStage | null>(null)

  const open = funnel.stages.filter((s) => s.stageType === 'open')
  const closing = funnel.stages.filter((s) => s.stageType !== 'open')
  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderFunnelStages(funnel.id, ids),
    onSuccess: onDone,
    onError: onFailed,
  })
  const remove = useMutation({
    mutationFn: (stageId: string) => removeFunnelStage(funnel.id, stageId),
    onSuccess: (saved) => {
      setRemoving(null)
      onDone(saved)
    },
    onError: (error) => {
      setRemoving(null)
      onFailed(error)
    },
  })

  /** Moves an open stage to another open stage's place and saves the whole order. */
  const moveTo = (stageId: string, targetIndex: number) => {
    const ids = moveId(open.map((s) => s.id), stageId, targetIndex)
    if (ids.join() !== open.map((s) => s.id).join()) reorder.mutate(ids)
  }

  const stageMenu = (stage: AdminStage): MenuAction[] => {
    const items: MenuAction[] = [
      { id: 'edit', label: t('adm.stage.edit'), icon: 'adm-edit', onSelect: () => setEditingStage(stage) },
    ]
    if (stage.stageType === 'open') {
      items.push({
        id: 'remove',
        label: t('admin.stage.remove'),
        icon: 'trash',
        danger: true,
        disabledReason: stage.openDeals > 0
          ? t('adm.stage.remove.deals', { count: stage.openDeals })
          : open.length <= 1 ? t('adm.stage.remove.last') : undefined,
        onSelect: () => setRemoving(stage),
      })
    }
    return items
  }
  const total = funnel.stages.reduce((sum, stage) => sum + stage.openDeals, 0)

  const row = (stage: AdminStage, index: number) => {
    const movable = mayEdit && stage.stageType === 'open'
    const swatch = colorToken(stage.color)
    return (
      <div
        key={stage.id}
        role="listitem"
        data-testid={`stage-${stage.code}`}
        className={[
          'sort-row',
          dragging === stage.id ? 'is-dragging' : '',
          editingStage?.id === stage.id ? 'is-selected' : '',
        ].filter(Boolean).join(' ')}
        draggable={movable}
        onDragStart={(event) => {
          setDragging(stage.id)
          event.dataTransfer?.setData('text/plain', stage.id)
        }}
        onDragEnd={() => setDragging(null)}
        onDragOver={(event) => {
          if (movable && dragging) event.preventDefault()
        }}
        onDrop={(event) => {
          event.preventDefault()
          const id = event.dataTransfer?.getData('text/plain') || dragging
          setDragging(null)
          if (movable && id && id !== stage.id) moveTo(id, index)
        }}
      >
        {mayEdit && (
          stage.stageType === 'open' ? (
            <Grip label={t('adm.stage.move', { name: stage.name })} disabled={reorder.isPending}
                  onMove={(step) => moveTo(stage.id, index + step)} />
          ) : (
            <Grip label={t('adm.stage.pinned', { name: stage.name })} locked />
          )
        )}
        <span className={stage.stageType === 'open' ? 'ord' : 'adm-w22'}>{stage.stageType === 'open' ? index + 1 : ''}</span>
        <span className="swatch" style={{ background: swatch ?? 'transparent', borderStyle: swatch ? undefined : 'dashed' }}
              title={t('adm.stage.colorOf', { color: colorName(t, stage.color) })} />
        <span className="adm-grow stack">
          <span className="strong ellipsis">{stage.name}</span>
          <span className="t-xs muted mono">{stage.code}</span>
        </span>
        <span className="num adm-col" style={{ width: 84 }}>{stage.probability} %</span>
        <span className="num adm-col" style={{ width: 92 }}>
          {stage.stageType !== 'open' || !stage.maxDaysInStage ? <span className="muted">—</span> : t('adm.stage.days', { count: stage.maxDaysInStage })}
        </span>
        <span className="num adm-col" style={{ width: 132 }}>
          {stage.stageType === 'open' ? <span className="strong">{stage.openDeals.toLocaleString(locale)}</span> : <span className="muted">—</span>}
        </span>
        <span className="adm-col" style={{ width: 96 }}>
          {stage.stageType === 'won' ? (
            <span className="badge b-success"><Icon name="check" />{t('admin.stage.type.won')}</span>
          ) : stage.stageType === 'lost' ? (
            <span className="badge"><Icon name="close" />{t('admin.stage.type.lost')}</span>
          ) : (
            <span className="badge"><span className="dot" style={{ color: 'var(--fg-muted)' }} />{t('admin.stage.type.open')}</span>
          )}
        </span>
        {mayEdit && <RowMenu label={t('adm.stage.actions', { name: stage.name })} items={stageMenu(stage)} />}
      </div>
    )
  }

  return (
    <section className="stack" style={{ gap: 10, minWidth: 0 }} aria-labelledby="st-h">
      <div className="row-wrap" style={{ gap: '8px 10px' }}>
        <span className="h2" id="st-h">{t('adm.funnel.stagesOf', { name: funnel.name })}</span>
        {funnel.isDefault && <span className="badge b-accent">{t('adm.funnel.default')}</span>}
        {!funnel.active && <span className="badge"><Icon name="adm-archive" />{t('adm.funnel.archived')}</span>}
        <span className="t-sm muted">{t('adm.funnel.counts', { open: open.length, closing: closing.length })}</span>
        <span className="spacer" />
        {mayEdit && <span className="t-sm muted">{t('adm.funnel.dragHint')}</span>}
        {mayEdit && <MoreMenu items={menu} label={t('adm.funnel.more', { name: funnel.name })} compact />}
      </div>
      <div className="sort-list" role="list" aria-label={t('admin.funnel.stages')}>
        <div className="sort-row adm-sort-head" aria-hidden="true">
          {mayEdit && <span className="adm-w24" />}
          <span className="adm-w22">№</span>
          <span style={{ width: 16, flex: 'none' }} />
          <span className="adm-grow">{t('admin.stage.name')}</span>
          <span className="num adm-col" style={{ width: 84 }}>{t('adm.stage.probability')}</span>
          <span className="num adm-col" style={{ width: 92 }}>{t('adm.stage.limit')}</span>
          <span className="num adm-col" style={{ width: 132 }}>{t('admin.stage.openDeals')}</span>
          <span className="adm-col" style={{ width: 96 }}>{t('admin.stage.type')}</span>
          {mayEdit && <span className="adm-w26" />}
        </div>
        {open.map((stage, index) => row(stage, index))}
        {closing.length > 0 && <div className="sort-sep"><Icon name="lock" size="xs" />{t('adm.stage.closingSep')}</div>}
        {closing.map((stage, index) => row(stage, open.length + index))}
      </div>
      <div className="row-wrap t-sm secondary" style={{ gap: '6px 16px', padding: '0 2px' }}>
        <span>{t('adm.funnel.total', { count: total.toLocaleString(locale) })}</span>
        <span className="muted">{t('adm.funnel.limitNote')}</span>
      </div>
      {editingStage && (
        <StageSheet funnel={funnel} stage={editingStage} onClose={() => setEditingStage(null)} onSaved={(saved) => {
          setEditingStage(null)
          onDone(saved)
        }} />
      )}
      <ConfirmModal
        open={removing != null}
        title={t('adm.stage.removeConfirm', { name: removing?.name ?? '' })}
        confirmLabel={t('admin.stage.remove')}
        pending={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
      >
        <p>{t('adm.stage.removeConfirm.text')}</p>
      </ConfirmModal>
    </section>
  )
}

interface StageDraft {
  code: string
  name: string
  /** 1-based place among the open stages, for a new stage only. */
  position: string
  probability: string
  maxDays: string
  color: string
}

function StageSheet({ funnel, stage, onClose, onSaved }: {
  funnel: AdminFunnel
  stage: AdminStage | null
  onClose: () => void
  onSaved: (funnel: AdminFunnel) => void
}) {
  const { t } = useApp()
  const formId = useId()
  const colorId = useId()
  const open = funnel.stages.filter((s) => s.stageType === 'open')
  const [draft, setDraft] = useState<StageDraft>(() => stage
    ? {
        code: stage.code,
        name: stage.name,
        position: '',
        probability: String(stage.probability),
        maxDays: stage.maxDaysInStage == null ? '' : String(stage.maxDaysInStage),
        color: stage.color ?? '',
      }
    : { code: '', name: '', position: String(open.length + 1), probability: '0', maxDays: '', color: '' })
  const [error, setError] = useState<string | null>(null)
  const terminal = stage != null && stage.stageType !== 'open'
  const probabilityChanged = stage != null && Number(draft.probability || '0') !== stage.probability

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: draft.name,
        probability: Number(draft.probability || '0'),
        // Empty limit means "no limit"; the server reads 0 the same way.
        maxDaysInStage: Number(draft.maxDays || '0'),
        color: draft.color,
      }
      return stage
        ? updateFunnelStage(funnel.id, stage.id, body)
        : addFunnelStage(funnel.id, { ...body, code: draft.code.trim(), position: Number(draft.position) })
    },
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })

  const index = stage ? open.findIndex((s) => s.id === stage.id) : -1
  return (
    <EditLayer
      kind="sheet"
      overline={stage && index >= 0
        ? t('adm.stage.overlineOf', { funnel: funnel.name, n: index + 1, of: open.length })
        : t('adm.stage.overline', { funnel: funnel.name })}
      title={stage ? stage.name : t('admin.stage.addForm')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending}
                            disabled={draft.name.trim() === '' || (!stage && draft.code.trim() === '')} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t(stage ? 'admin.stage.form' : 'admin.stage.addForm')}
            onSubmit={(event) => {
              event.preventDefault()
              setError(null)
              save.mutate()
            }}>
        <Field label={t('admin.stage.name')} htmlFor="st-name" required help={t('adm.stage.name.help')}>
          <input id="st-name" className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </Field>
        {stage ? (
          <Field label={t('admin.stage.code')}>
            <ReadonlyValue mono>{stage.code}</ReadonlyValue>
          </Field>
        ) : (
          <div className="form-grid">
            <Field label={t('admin.stage.code')} htmlFor="st-code" required>
              <input id="st-code" className="input mono" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
            </Field>
            <Field label={t('admin.stage.position')} htmlFor="st-pos" help={t('adm.stage.position.help')}>
              <select id="st-pos" className="input" value={draft.position} onChange={(e) => setDraft({ ...draft, position: e.target.value })}>
                <option value="1">{t('admin.stage.first')}</option>
                {open.map((candidate, at) => (
                  <option key={candidate.id} value={String(at + 2)}>{t('admin.stage.after', { name: candidate.name })}</option>
                ))}
              </select>
            </Field>
          </div>
        )}
        <div className="field">
          <span className="label" id={colorId}>{t('admin.stage.color')}</span>
          <SwatchPick value={draft.color} labelledBy={colorId} onChange={(color) => setDraft({ ...draft, color })} />
          <span className="help">{t('adm.stage.color.help', { color: colorName(t, draft.color || null) })}</span>
        </div>
        <div className="form-grid">
          <Field label={t('admin.stage.probability')} htmlFor="st-prob" dirty={probabilityChanged}
                 help={terminal ? t('adm.stage.probability.terminal') : stage ? t('adm.stage.was', { value: stage.probability }) : undefined}>
            {/* Won is 100% and lost 0% by definition — the server refuses anything else. */}
            <div className="input">
              <input id="st-prob" className="adm-bare is-num" type="number" min={0} max={100} disabled={terminal}
                     value={draft.probability} onChange={(e) => setDraft({ ...draft, probability: e.target.value })} />
              <span className="suffix">%</span>
            </div>
          </Field>
          <Field label={t('adm.stage.limitDays')} htmlFor="st-lim" help={t('adm.stage.limit.help')}>
            <div className="input">
              <input id="st-lim" className="adm-bare is-num" type="number" min={0} placeholder={t('admin.stage.noSla')}
                     disabled={terminal} value={draft.maxDays} onChange={(e) => setDraft({ ...draft, maxDays: e.target.value })} />
              <span className="suffix">{t('adm.stage.daysSuffix')}</span>
            </div>
          </Field>
        </div>
        {probabilityChanged && stage && stage.openDeals > 0 && (
          <div className="alert alert-warning" role="status" style={{ alignItems: 'flex-start' }}>
            <Icon name="alert" />
            <span className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
              <span className="fg">{t('adm.stage.probabilityMoves', { count: stage.openDeals })}</span>
              <span className="t-sm">{t('adm.stage.probabilityMoves.text')}</span>
            </span>
          </div>
        )}
        {stage && (
          <Field label={t('admin.stage.type')} help={t('adm.stage.type.help')}>
            <ReadonlyValue>{t(`admin.stage.type.${stage.stageType}`)}</ReadonlyValue>
          </Field>
        )}
        {stage && stage.stageType === 'open' && (
          <dl className="kv" style={{ paddingTop: 4, borderTop: '1px solid var(--border)' }}>
            <dt style={{ paddingTop: 10 }}>{t('adm.stage.now')}</dt>
            <dd style={{ paddingTop: 10 }}>{t('adm.stage.openCount', { count: stage.openDeals })}</dd>
          </dl>
        )}
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}

function NewFunnelModal({ onClose, onSaved }: { onClose: () => void; onSaved: (funnel: AdminFunnel) => void }) {
  const { t } = useApp()
  const formId = useId()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: () => createAdminFunnel({ code: code.trim(), name }),
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })
  return (
    <EditLayer kind="modal" large={false} overline={t('nav.adminFunnels')} title={t('admin.funnel.form')} onClose={onClose}
               footer={<LayerButtons onCancel={onClose} saveLabel={t('adm.funnel.create')} pending={create.isPending}
                                     disabled={code.trim() === '' || name.trim() === ''} form={formId} />}>
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('admin.funnel.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        create.mutate()
      }}>
        <Field label={t('admin.funnel.name')} htmlFor="fn-name" required>
          <input id="fn-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('admin.funnel.code')} htmlFor="fn-code" required>
          <input id="fn-code" className="input mono" value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
        <div className="alert alert-neutral"><Icon name="adm-info" /><span>{t('admin.funnel.createHint')}</span></div>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}

function RenameFunnelModal({ funnel, onClose, onSaved }: { funnel: AdminFunnel; onClose: () => void; onSaved: (funnel: AdminFunnel) => void }) {
  const { t } = useApp()
  const formId = useId()
  const [name, setName] = useState(funnel.name)
  const [description, setDescription] = useState(funnel.description ?? '')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => updateAdminFunnel(funnel.id, { name, description }),
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })
  return (
    <EditLayer kind="modal" large={false} overline={t('nav.adminFunnels')} title={t('adm.funnel.rename')} onClose={onClose}
               footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending}
                                     disabled={name.trim() === ''} form={formId} />}>
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('admin.funnel.props')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <Field label={t('admin.funnel.name')} htmlFor="fr-name" required>
          <input id="fr-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('adm.funnel.description')} htmlFor="fr-desc">
          <textarea id="fr-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
